import { useCallback, useEffect, useRef, useState } from 'react';
import { useMusicState } from './storeHooks/useMusicState';
import useAlbumData from './useAlbumData';

interface UseSerenityInfiniteScrollOptions {
  rootMargin?: string;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
  genre?: string;
  search?: string;
}

interface UseSerenityInfiniteScrollReturn {
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  reset: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  albums: any[]; // Using the store data
}

/**
 * Custom hook for managing infinite scroll of serenity albums using the Redux store
 * This ensures both SerenityWellness and SerenityGrid components share the same data
 */
export const useSerenityInfiniteScroll = (
  options: UseSerenityInfiniteScrollOptions = {},
): UseSerenityInfiniteScrollReturn => {
  const { rootMargin = '200px', scrollContainerRef, enabled = true, genre, search } = options;

  // Get store state and actions
  const {
    serenityPlaylist: albums,
    appendSerenityPlaylist,
    setSerenityPlaylist,
  } = useMusicState();

  const { loadSerenityAlbumsPaginated } = useAlbumData();

  // Local state for infinite scroll
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs for managing state and observer
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const initialLoadTriggered = useRef(false);

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || !enabled) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await loadSerenityAlbumsPaginated(12, offsetRef.current, genre, search);

      if (offsetRef.current === 0) {
        // Initial load - replace items
        setSerenityPlaylist(result?.items || []);
      } else {
        // Subsequent loads - append items
        appendSerenityPlaylist(result?.items || []);
      }

      const hasNext = result?.hasNext || false;
      setHasMore(hasNext);
      offsetRef.current += result?.items?.length || 0;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load more items';
      setError(errorMessage);
      console.error('Error loading more serenity items:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [
    loadSerenityAlbumsPaginated,
    hasMore,
    enabled,
    appendSerenityPlaylist,
    setSerenityPlaylist,
  ]);

  // Reset function
  const reset = useCallback(() => {
    setSerenityPlaylist([]);
    offsetRef.current = 0;
    setHasMore(true);
    setError(null);
    setLoading(false);
    loadingRef.current = false;
    setInitialLoadDone(false);
    initialLoadTriggered.current = false;
  }, [setSerenityPlaylist]);

  // Reset data when genre or search changes and trigger new load
  useEffect(() => {
    console.log('Serenity genre/search changed, resetting data and triggering load:', { genre, search });

    // Reset the data
    setSerenityPlaylist([]);
    offsetRef.current = 0;
    setHasMore(true);
    setError(null);
    setInitialLoadDone(false);
    initialLoadTriggered.current = false;

    // Trigger immediate load with selected genre/search
    if (enabled) {
      console.log('Triggering loadMore for serenity genre/search change');
      const triggerLoad = async () => {
        if (!loadingRef.current) {
          loadingRef.current = true;
          setLoading(true);
          setError(null);

          try {
            const result = await loadSerenityAlbumsPaginated(12, 0, genre, search);
            console.log('Serenity API result for genre/search change:', { result, genre, search });

            setSerenityPlaylist(result?.items || []);
            const hasNext = result?.hasNext || false;
            setHasMore(hasNext);
            offsetRef.current = (result?.items || []).length;
            setInitialLoadDone(true);
          } catch (err) {
            console.error('Error loading serenity albums for genre/search:', err);
            setError(err instanceof Error ? err.message : 'Failed to load albums');
            setHasMore(false);
          } finally {
            setLoading(false);
            loadingRef.current = false;
          }
        }
      };

      triggerLoad();
    } else {
      console.log('Not triggering loadMore - enabled is false');
    }
  }, [genre, search, enabled, loadSerenityAlbumsPaginated]);





  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!enabled || !hasMore || !initialLoadDone || !sentinelRef.current)
      return;

    // Disconnect any existing observer first
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingRef.current && hasMore) {
          loadMore();
        }
      },
      {
        root: scrollContainerRef?.current || null,
        rootMargin,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    enabled,
    hasMore,
    initialLoadDone,
    loadMore,
    rootMargin,
    scrollContainerRef,
  ]);

  // Initial load effect
  useEffect(() => {
    if (!enabled || initialLoadTriggered.current) return;

    if (!initialLoadDone && albums.length === 0) {
      initialLoadTriggered.current = true;
      // Set loading to true immediately for initial load to show skeleton
      setLoading(true);
      loadingRef.current = true;
      loadMore(); // Enable initial load
    } else if (albums.length > 0 && !initialLoadDone) {
      // If albums already exist in store, sync the offset
      initialLoadTriggered.current = true;
      setInitialLoadDone(true);
      offsetRef.current = albums.length;
    }
  }, [enabled, initialLoadDone, albums.length, loadMore]);

  return {
    loading,
    hasMore,
    error,
    loadMore,
    reset,
    sentinelRef,
    albums,
  };
};

export default useSerenityInfiniteScroll;
